// SEC-202: sanitize admin-authored video embed HTML before dropping it
// into `dangerouslySetInnerHTML`. The embed field is written by staff
// with the `modules.edit` / `intros.edit` capability (which the default
// Contributor role has) — that lower-trust tier must not be able to
// slip `<script>` tags or `onerror` handlers into the module player,
// where the content later runs in every member's browser.
//
// We keep the exact fields needed for Vimeo / YouTube / Loom iframe
// embeds and strip everything else. The wrapper `<div>` with padding-
// based aspect ratio is also preserved, so the existing rendering
// behavior is unchanged for legitimate embeds.
import DOMPurify from "dompurify";

const IFRAME_HOST_ALLOWLIST = [
  "player.vimeo.com",
  "www.youtube.com",
  "youtube.com",
  "www.youtube-nocookie.com",
  "youtube-nocookie.com",
  "www.loom.com",
  "loom.com",
];

function iframeSrcIsAllowed(src) {
  try {
    const u = new URL(src, window.location.origin);
    if (u.protocol !== "https:") return false;
    return IFRAME_HOST_ALLOWLIST.includes(u.hostname);
  } catch {
    return false;
  }
}

export function sanitizeVideoEmbed(html) {
  if (!html || typeof html !== "string") return "";
  const clean = DOMPurify.sanitize(html, {
    // Allow the layout `<div>` and the video `<iframe>` only.
    ALLOWED_TAGS: ["div", "iframe", "p"],
    ALLOWED_ATTR: [
      "class",
      "style",
      "src",
      "width",
      "height",
      "frameborder",
      "allow",
      "allowfullscreen",
      "title",
      "loading",
      "referrerpolicy",
      "sandbox",
      "aria-label",
    ],
    // Absolutely no scripts, event handlers, or nested SVG/data URIs.
    FORBID_TAGS: ["script", "style", "link", "meta", "base", "object", "embed", "form"],
    FORBID_ATTR: [
      "onerror", "onclick", "onload", "onfocus", "onmouseover", "onmouseenter",
      "onmouseout", "onmouseleave", "onchange", "onsubmit", "onkeydown",
      "onkeyup", "onkeypress",
    ],
    RETURN_TRUSTED_TYPE: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
  // Post-process: enforce iframe host allowlist on the sanitized HTML.
  const tpl = document.createElement("template");
  tpl.innerHTML = clean;
  const frames = tpl.content.querySelectorAll("iframe");
  frames.forEach((f) => {
    const src = f.getAttribute("src") || "";
    if (!iframeSrcIsAllowed(src)) {
      f.remove();
    } else {
      // Force sandbox + referrer-policy on every embed as defense-in-depth.
      if (!f.getAttribute("referrerpolicy")) {
        f.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
      }
    }
  });
  return tpl.innerHTML;
}
